import {
  isIfConditionStepId,
  isPlaceHolderStepId,
  TriggerForEditor,
} from "@dots-projects/types";
import { useCallback, useRef, useState } from "react";
import {
  useStore,
  getSmoothStepPath,
  Node,
  EdgeProps,
  Position,
  internalsSymbol,
  EdgeText,
  useEdges,
  Edge,
} from "reactflow";
import invariant from "ts-invariant";
import { NODE_WIDTH } from "./createLayout";
import { addFloatingPlaceholder, removeNode } from "./reducer/actionCreators";
import { TypedNode } from "./reducer/stateUtils";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { motion } from "framer-motion";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { SVG } from "@svgdotjs/svg.js";

/**
 * This file is based on https://reactflow.dev/docs/examples/edges/simple-floating-edges/
 *
 * I just put some simple tweaks in this file to make the edges look roughly good. We should improve this at a later stage for polishing of the look and feel.
 *
 */

function getHandleCoordsByPosition(
  node: Node,
  handlePosition: Position,
  sourceOrTarget: "source" | "target" = "source",
  handleIndexOnSameTarget = 0,
  handleCountOnSameTarget = 1
) {
  invariant(node.positionAbsolute && node.width && node.height);
  // all handles are from type source, that's why we use handleBounds.source here
  //@ts-ignore
  const handle = node[internalsSymbol].handleBounds[sourceOrTarget].find(
    (h) => h.position === handlePosition
  );
  invariant(
    handle,
    `Can't find handle: id:${node.id}, type:${node.type} position:${handlePosition}, ${sourceOrTarget}`
  );

  let offsetX = handle.width / 2;
  let offsetY = handle.height / 2;

  // this is a tiny detail to make the markerEnd of an edge visible.
  // The handle position that gets calculated has the origin top-left, so depending which side we are using, we add a little offset
  // when the handlePosition is Position.Right for example, we need to add an offset as big as the handle itself in order to get the correct position
  switch (handlePosition) {
    case Position.Left:
      offsetX = 0;
      break;
    case Position.Right:
      offsetX = handle.width;
      break;
    case Position.Top:
      offsetY = 0;
      if (sourceOrTarget === "target") {
        const gap = 16;
        offsetX +=
          -((handle.width + gap) * (handleCountOnSameTarget - 1)) / 2 +
          handleIndexOnSameTarget * (gap + handle.width);
      }
      break;
    case Position.Bottom:
      offsetY = handle.height;
      break;
  }

  const x = node.positionAbsolute.x + handle.x + offsetX;
  const y = node.positionAbsolute.y + handle.y + offsetY;

  return [x, y];
}

/**
 * returns the parameters you need to create an edge.
 *
 * Note: The code below include some adjustments to make the layout look OK for some given configurations, such as "Ask a question" and "Send survey". When we have time, we need to write smarter, more general logic that creates proper edge params for any arbitary situation.
 */
function getEdgeParams(
  source: TypedNode,
  target: TypedNode,
  handleIndexOnSameTarget: number,
  handleCountOnSameTarget: number
) {
  let sourcePos = Position.Bottom;
  let targetPos = Position.Top;
  let centerX, centerY;
  const isSourceBelowTarget = source.position.y > target.position.y;

  if (isSourceBelowTarget) {
    sourcePos = Position.Right;
    targetPos = Position.Right;
    if (target.type === "condition") {
      sourcePos = Position.Left;
      targetPos = Position.Left;
    }
  }
  let [sx, sy] = getHandleCoordsByPosition(source, sourcePos);
  let handleIndex = 0;
  let handleCount = 1;
  switch (target.type) {
    case "step":
      handleIndex = handleIndexOnSameTarget;
      handleCount = handleCountOnSameTarget;
      break;
    case "if-else":
      if (handleCountOnSameTarget > 2) {
        handleIndex = handleIndexOnSameTarget;
        handleCount = handleCountOnSameTarget;
      }
      break;
  }
  let [tx, ty] = getHandleCoordsByPosition(
    target,
    targetPos,
    "target",
    handleIndex,
    handleCount
  );

  let labelX = tx,
    labelY = ty - 18;

  if (isSourceBelowTarget) {
    if (source.type === "condition") {
      sx -= 95;
    }
    centerX = Math.max(sx, tx) + 30;
    labelX = centerX - 50;
    labelY = sy;

    if (target.type === "condition") {
      tx += 78;
      centerX = Math.min(sx, tx) - 30;
    }
  }

  return {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
    centerX,
    centerY,
    labelX,
    labelY,
  };
}

/**
 * Returns relevant info like node and transition information for the
 * a floating placeholder to be used as reference for inserted steps
 * @param sourceNode
 * @param targetNode
 * @param trigger
 * @param edges
 * @returns
 */
function getFloatingPlaceholderInfo(
  sourceNode: TypedNode,
  targetNode: TypedNode,
  trigger: TriggerForEditor,
  edges: Edge[]
) {
  let parentStep;
  let parentStepId;
  switch (sourceNode.type) {
    case "trigger":
      parentStepId = sourceNode.id;
      break;
    case "step":
    case "if-else":
    case "placeholder":
    case "placeholder-published":
      // sourceNode corresponds to the parent step
      parentStep = trigger.steps.find((s) => s.id === sourceNode.id);
      parentStepId = parentStep?.id;
      break;
    case "condition": {
      // find the actual step via the edge attached to the condition node
      // if the condition is right after an idle step, get the idle step as parent
      if (sourceNode.data.stepId.includes("idle")) {
        parentStep = trigger.steps.find((s) => s.id === sourceNode.data.stepId);
      } else {
        // else get the step prior
        const edge = edges.find(
          (e) => e.target === (sourceNode as TypedNode).id
        );
        parentStep = trigger.steps.find((s) => s.id === edge?.source);
      }
      parentStepId = parentStep?.id;
      break;
    }
    default:
      throw new Error(
        `Invalid sourceNode type for floating placeholder: ${
          (sourceNode as TypedNode).type
        }`
      );
  }

  // When there are two transitions with the same source and target, we get the first transition. This isn't a very useful use case anyways.
  const transitionIndex = parentStep?.transitions.findIndex(
    (t) => t.targetStepId === targetNode.id
  );
  const onElseBranch =
    sourceNode.type === "if-else" &&
    parentStep?.if?.elseStepId === targetNode.id;

  return {
    onElseBranch,
    parentStepId,
    transitionIndex,
    parentStep,
  };
}

function getEdgeButtonPositions(
  path: string,
  buttonCount: number,
  buttonSize: number,
  gap: number
) {
  const svgPath = SVG().path(path);
  const pathLength = svgPath.length();

  let startLength =
    (pathLength - buttonCount * (buttonSize + gap) - gap) / 2 + 10;

  const points = [];
  for (let i = 0; i < buttonCount; i++) {
    const point = svgPath.pointAt(startLength);
    points.push(point);
    startLength += buttonSize + gap;
  }

  return points;
}

/**
 *
 * Custom edge component that can adjust handle position based on the relative positioning of source and target nodes.
 *
 * This component is based on https://reactflow.dev/docs/examples/edges/simple-floating-edges/, but it's not as flexible and we added some changes that are specific in our situation, e.g. condition nodes.
 *
 */
export function SimpleFloatingEdge({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  data,
}: EdgeProps) {
  const sourceNode = useStore(
    useCallback((store) => store.nodeInternals.get(source), [source])
  ) as TypedNode;
  const targetNode = useStore(
    useCallback((store) => store.nodeInternals.get(target), [target])
  ) as TypedNode;

  if (!sourceNode || !targetNode) {
    return null;
  }

  invariant(
    typeof data.handleIndexOnSameTarget === "number",
    `Expect edge.data.handleIndexOnSameTarget to be a number, but got ${data.handleIndexOnSameTarget}`
  );
  invariant(
    typeof data.handleCountOnSameTarget === "number",
    `Expect edge.data.handleCountOnSameTarget to be a number, but got ${data.handleCountOnSameTarget}`
  );

  const {
    sx,
    sy,
    tx,
    ty,
    sourcePos,
    targetPos,
    centerX,
    centerY,
    labelX,
    labelY,
  } = getEdgeParams(
    sourceNode,
    targetNode,
    data.handleIndexOnSameTarget,
    data.handleCountOnSameTarget
  );

  const [path] = getSmoothStepPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
    targetX: tx,
    targetY: ty,
    centerX,
    centerY,
  });

  /**
   * Floating placeholder values
   */
  const { state, dispatch } = useTriggerEditorStateDispatch();
  const edges = useEdges();
  const floatingPlaceholderId = `${id}-floating-placeholder`;

  const canAdd = () => {
    const { onElseBranch, parentStep } = getFloatingPlaceholderInfo(
      sourceNode,
      targetNode,
      state.trigger.current,
      edges
    );
    // TODO: These rules (and canDelete) are rather adhoc, we should probably have a more systematic way that can be shared across the frontend.
    const alreadyHasPlaceholder =
      isPlaceHolderStepId(source) || isPlaceHolderStepId(target);
    const targetStep = state.trigger.current.steps.find((s) => s.id === target);
    const liveTriggerType = state.trigger.current.isDraft
      ? state.trigger.current.liveTriggerConfigs.type
      : "";
    const cannotAdds =
      parentStep?.type === "send_multiple_select" ||
      // Cannot add steps between a send_survey and a test_captcha (or open_modal), if the trigger is not a button_click or command
      (parentStep?.type === "send_survey" &&
        ["test_captcha", "open_modal"].includes(targetStep?.type ?? "") &&
        !["button_click", "command"].includes(liveTriggerType));
    return (
      state.trigger.current.isDraft &&
      !onElseBranch &&
      !alreadyHasPlaceholder &&
      // if-else is a step as well
      (targetNode.type === "step" || targetNode.type === "if-else") &&
      !cannotAdds
    );
  };

  const canDelete = () => {
    return (
      state.trigger.current.isDraft &&
      sourceNode.type !== "trigger" &&
      targetNode.type !== "condition" &&
      sourceNode.type !== "if-else" &&
      targetNode.type !== "if-else"
    );
  };

  const createFloatingPlaceholder = () => {
    const { parentStepId, transitionIndex } = getFloatingPlaceholderInfo(
      sourceNode,
      targetNode,
      state.trigger.current,
      edges
    );
    const placeholderX = placeholderButtonPosition.x - EDGE_BUTTON_SIZE / 2;
    const placeholderY = placeholderButtonPosition.y - EDGE_BUTTON_SIZE / 2;
    invariant(parentStepId);
    dispatch(
      addFloatingPlaceholder(
        floatingPlaceholderId,
        {
          parentStepId,
          isParentTriggerRoot: sourceNode.type === "trigger",
          transitionIndex,
        },
        { x: placeholderX, y: placeholderY }
      )
    );
  };

  const edgeButtonPositions = getEdgeButtonPositions(
    path,
    canAdd() && canDelete() ? 2 : 1,
    EDGE_BUTTON_SIZE,
    5
  );
  const deleteButtonPosition = edgeButtonPositions[0];
  const placeholderButtonPosition = canDelete()
    ? edgeButtonPositions[1]
    : edgeButtonPositions[0];

  const [controlsVisible, setControlsVisible] = useState(false);

  const deleteEdge = () => {
    let stepId = sourceNode.id;
    switch (sourceNode.type) {
      case "step":
      case "if-else":
        stepId = sourceNode.id;
        break;
      case "condition":
        stepId = sourceNode.data.stepId;
        break;
    }
    const transitionIndex = data?.transitionIndex ?? 0;
    dispatch({
      type: "delete_transition",
      payload: { stepId, transitionIndex },
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout>();
  return (
    <>
      <motion.g
        id={id}
        onMouseEnter={(event) => {
          if (event.buttons !== 0) return; // Don't show the controls if the user is dragging
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setControlsVisible(true);
          if (canAdd()) {
            createFloatingPlaceholder();
          }
        }}
        onMouseLeave={() => {
          // Add a delay before hiding the controls, so that the user can click on them
          timeoutRef.current = setTimeout(() => {
            setControlsVisible(false);
            // The float placeholder is removed in PlaceholderNode
          }, 1000);
        }}
      >
        <path
          className="react-flow__edge-path"
          d={path}
          strokeWidth={5}
          markerEnd={markerEnd}
          style={style}
        />
        <EdgeText
          x={labelX + (data.labelOffsetX ?? 0)}
          y={labelY + (data.labelOffsetY ?? 0)}
          label={label}
        />
        {controlsVisible && (
          <>
            {/* Delete button */}
            {canDelete() && (
              <foreignObject
                x={deleteButtonPosition.x - EDGE_BUTTON_SIZE / 2}
                y={deleteButtonPosition.y - EDGE_BUTTON_SIZE / 2}
                width={100}
                height={100}
              >
                <EdgeButton
                  icon={<DeleteOutlined />}
                  size={EDGE_BUTTON_SIZE}
                  onClick={deleteEdge}
                />
              </foreignObject>
            )}
            {/* The "+" button is added as a "floating" placeholder so that it can be selectable. */}
          </>
        )}
      </motion.g>
    </>
  );
}

const EDGE_BUTTON_SIZE = 22;

export function EdgeButton({
  icon,
  size = EDGE_BUTTON_SIZE,
  ...props
}: React.HTMLProps<HTMLDivElement> & {
  icon: React.ReactNode;
  size?: number;
}) {
  return (
    <div
      className="border border-black rounded-full flex items-center justify-center bg-white hover:shadow-md hover:border-dots-vibrant-orange hover:text-dots-vibrant-orange"
      style={{ width: size, height: size }}
      {...props}
    >
      {icon}
    </div>
  );
}

export default SimpleFloatingEdge;
